<template>
  <v-container class="booking-page pa-0" fluid>
    <!-- ========== Hero Header Section ========== -->
    <header class="hero-header">
      <!-- Background Image -->
      <video v-if="isTnto" class="hero-header__bg hero-header__video" :src="require('../../assets/TNTO-video.webm')" autoplay muted loop playsinline></video>
      <div class="hero-header__bg" v-else
        :style="{ 'background-image': 'url(' + require('../../assets/img/golf_course_2.jpg') + ')' }">
      </div>
      <div class="hero-header__overlay"></div>

      <div class="hero-header__content">
        <!-- Top Navigation -->
        <div class="hero-nav">

        </div>

        <!-- Titles -->
        <div class="hero-titles mb-16">
          <h1 class="hero-title">Reserva tu ronda</h1>
          <p class="hero-subtitle">Tu próxima gran ronda te espera</p>

        </div>
      </div>
    </header>

    <!-- ========== Loader Overlay ========== -->
    <div v-if="loadingTarifas" class="booking-loader-overlay">
      <div class="booking-loader-card">
        <v-progress-circular indeterminate color="#059669" size="48" width="4" />
        <div class="booking-loader-text">Loading Tee Sheet...</div>
        <div class="booking-loader-sub">Fetching live rates &amp; availability</div>
      </div>
    </div>

    <!-- ========== Main Booking Container (Overlaps Hero) ========== -->
    <div class="booking-main-container">
      <v-form ref="form" @submit.prevent="bookNow">
        <!-- ========== Mockup Stepper ========== -->
        <div class="mockup-stepper">
          <div class="mockup-stepper__step" @click="step === 'sendPerData' ? (step = 'dates') : null">
            <div class="mockup-stepper__circle"
              :class="{ 'active': step === 'config' || step === 'dates' || step === 'sendPerData' }">
              1
            </div>
            <div class="mockup-stepper__label"
              :class="{ 'active': step === 'config' || step === 'dates' || step === 'sendPerData' }">
              Jugadores &amp; Fecha
            </div>
          </div>
          <div class="mockup-stepper__line" :class="{ 'active': step === 'dates' || step === 'sendPerData' }"></div>

          <div class="mockup-stepper__step" @click="step === 'sendPerData' ? (step = 'dates') : null">
            <div class="mockup-stepper__circle" :class="{ 'active': step === 'dates' || step === 'sendPerData' }">
              2
            </div>
            <div class="mockup-stepper__label" :class="{ 'active': step === 'dates' || step === 'sendPerData' }">
              Tee Time
            </div>
          </div>
          <div class="mockup-stepper__line" :class="{ 'active': step === 'sendPerData' }"></div>

          <div class="mockup-stepper__step">
            <div class="mockup-stepper__circle" :class="{ 'active': step === 'sendPerData' }">
              3
            </div>
            <div class="mockup-stepper__label" :class="{ 'active': step === 'sendPerData' }">
              Checkout
            </div>
          </div>
        </div>

        <v-tabs-items v-model="step" class="booking-tabs-container">
          <!-- ============================================================ -->
          <!-- STEP 1: Configuration (Mobile full view when needed)         -->
          <!-- ============================================================ -->
          <v-tab-item v-if="$vuetify.breakpoint.mdAndDown" value="config" class="transparent">
            <div class="booking-inner-wrap pt-0">
              <v-card class="modern-card pa-4 pa-sm-6" elevation="0">
                <!-- Title -->
                <div class="card-heading mb-4">
                  <div class="card-heading__icon">
                    <v-icon color="#059669">mdi-account-group</v-icon>
                  </div>
                  <div>
                    <h2 class="card-heading__title">1. Number of Players</h2>
                    <p class="card-heading__sub">Choose golfers in your party</p>
                  </div>
                </div>

                <!-- Players Selector (Mobile Segmented) -->
                <div class="players-grid mb-6">
                  <button v-for="(players, plIndx) in playerConf" :key="plIndx" type="button" class="player-choice-btn"
                    :class="{ 'player-choice-btn--active': playersNumber == players.value }" @click="
                      playersNumber = players.value;
                    changePlayersNumber();
                    ">
                    <v-icon :color="playersNumber == players.value ? 'white' : '#059669'" class="mb-1">
                      {{ players.icon }}
                    </v-icon>
                    <span class="player-choice-btn__val">{{ players.value }}</span>
                    <span class="player-choice-btn__label">{{ players.title }}</span>
                  </button>
                </div>

                <v-divider class="my-5 divider-subtle" />

                <!-- Game Type / Holes -->
                <div class="card-heading mb-3">
                  <div class="card-heading__icon">
                    <v-icon color="#059669">mdi-flag-triangle</v-icon>
                  </div>
                  <div>
                    <h2 class="card-heading__title">2. Game Type</h2>
                    <p class="card-heading__sub">Select 18 or 9 holes</p>
                  </div>
                </div>

                <v-select v-model="hoyos" :items="tiposJuego" item-text="nombre" item-value="id" label="Game Type"
                  outlined dense color="#059669" class="modern-input mb-6" @onChange="changeHoyos" />

                <v-divider class="my-5 divider-subtle" />

                <!-- Date Picker -->
                <div class="card-heading mb-3">
                  <div class="card-heading__icon">
                    <v-icon color="#059669">mdi-calendar-month</v-icon>
                  </div>
                  <div>
                    <h2 class="card-heading__title">3. Select Date</h2>
                    <p class="card-heading__sub">Pick your reservation date</p>
                  </div>
                </div>

                <div class="modern-datepicker-wrap mb-6">
                  <v-date-picker v-model="inputDate" :allowed-dates="allowedDates" color="#059669"
                    header-color="#064E3B" no-title scrollable full-width @input="selectDate" />
                </div>

                <!-- Continue CTA -->
                <v-btn color="#059669" dark block x-large depressed class="modern-btn-primary" @click="step = 'dates'">
                  <span>Ver Horarios Disponibles</span>
                  <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
              </v-card>
            </div>
          </v-tab-item>

          <!-- ============================================================ -->
          <!-- STEP 2: Tee Times & Dates View                                -->
          <!-- ============================================================ -->
          <v-tab-item value="dates" class="transparent">
            <div class="booking-inner-wrap pt-0">

              <!-- Desktop Top Configuration Capsule Bar -->
              <v-card v-if="$vuetify.breakpoint.mdAndUp" class="modern-card config-capsule mb-6" elevation="0">
                <v-row align="center" class="pa-4" justify="space-between">
                  <!-- Players Selector -->
                  <v-col cols="4" class="py-1">
                    <div class="capsule-field">
                      <div class="capsule-field__label">
                        <v-icon x-small color="#059669" class="mr-1">mdi-account-group</v-icon>
                        PLAYERS
                      </div>
                      <v-select v-model="playersNumber" :items="playerConf" item-value="value" label="Select players"
                        color="#059669" hide-details outlined dense class="modern-input"
                        @change="changePlayersNumber()">
                        <template #item="{ item }">
                          <v-icon small class="mr-2" color="#059669">{{ item.icon }}</v-icon>
                          <span class="font-weight-medium">{{ item.value }} {{ item.title }}</span>
                        </template>
                        <template #selection="{ item }">
                          <v-icon small class="mr-2" color="#059669">{{ item.icon }}</v-icon>
                          <span class="font-weight-medium">{{ item.value }} {{ item.title }}</span>
                        </template>
                      </v-select>
                    </div>
                  </v-col>

                  <!-- Holes / Game Type -->
                  <v-col cols="4" class="py-1">
                    <div class="capsule-field">
                      <div class="capsule-field__label">
                        <v-icon x-small color="#059669" class="mr-1">mdi-flag-triangle</v-icon>
                        GAME TYPE
                      </div>
                      <v-select v-model="hoyos" :items="tiposJuego" item-text="nombre" item-value="id" label="Game Type"
                        color="#059669" outlined dense hide-details class="modern-input" @onChange="changeHoyos" />
                    </div>
                  </v-col>

                  <!-- Date Picker Input -->
                  <v-col cols="4" class="py-1">
                    <div class="capsule-field">
                      <div class="capsule-field__label">
                        <v-icon x-small color="#059669" class="mr-1">mdi-calendar</v-icon>
                        DATE
                      </div>
                      <calendar-input label="Select Date" initialPicker="DATE" :allowedDates="allowedDates"
                        :date="inputDate" class="modern-input" @date="selectDate" />
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Mobile Mockup Style Quick Filters -->
              <div v-else class="mockup-filters mb-6">
                <div class="mockup-filter-btn" @click="step = 'config'">
                  <v-icon small color="#0A5C36" class="mr-1">mdi-calendar-outline</v-icon>
                  <span class="mockup-filter-text">{{ input_date.format("DD MMM YY") }}</span>
                </div>
                <div class="mockup-filter-btn" @click="step = 'config'">
                  <v-icon small color="#0A5C36" class="mr-1">mdi-account</v-icon>
                  <span class="mockup-filter-text">{{ playersNumber }} pax</span>
                </div>
                <div class="mockup-filter-btn" @click="step = 'config'">
                  <v-icon small color="#0A5C36" class="mr-1">mdi-flag-triangle</v-icon>
                  <span class="mockup-filter-text">{{tiposJuego.find(t => t.id === hoyos) ? tiposJuego.find(t => t.id
                    ===
                    hoyos).nombre : 'Juego'}}</span>
                </div>
              </div>

              <!-- Hotel Range Selection (Golf Included vs Guest Paid) -->
              <div v-if="isHotel" class="hotel-range-section mb-6">
                <div class="section-title-wrap mb-3">
                  <v-icon color="#059669" class="mr-2">mdi-swap-horizontal</v-icon>
                  <h3 class="section-title">Tipo de Reservación</h3>
                </div>

                <v-row dense>
                  <v-col cols="12" sm="6">
                    <div class="range-card" :class="{ 'range-card--active': typePaid == 1 }" @click="selectPaid(1)">
                      <div class="range-card__check">
                        <v-icon v-if="typePaid == 1" small color="white">mdi-check</v-icon>
                      </div>
                      <div class="range-card__content">
                        <div class="range-card__badge">Hotel Benefit</div>
                        <div class="range-card__name">GOLF INCLUIDO</div>
                        <div class="range-card__desc">Golf incluido en su estancia</div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6">
                    <div class="range-card" :class="{ 'range-card--active': typePaid == 2 }" @click="selectPaid(2)">
                      <div class="range-card__check">
                        <v-icon v-if="typePaid == 2" small color="white">mdi-check</v-icon>
                      </div>
                      <div class="range-card__content">
                        <div class="range-card__badge range-card__badge--direct">Pago Directo</div>
                        <div class="range-card__name">GUEST PAID</div>
                        <div class="range-card__desc">Golf pagado por el cliente</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- Promotional Code (Hotel Included) -->
              <div v-if="typePaid === 1" class="promo-section mb-6">
                <div class="section-title-wrap mb-2">
                  <v-icon color="#059669" class="mr-2">mdi-ticket-percent-outline</v-icon>
                  <h3 class="section-title">Código Promocional</h3>
                </div>
                <div v-if="errors.code" class="modern-error-banner mb-3">
                  <v-icon small color="#EF4444" class="mr-2">mdi-alert-circle</v-icon>
                  El código promocional es obligatorio
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="promotionalCode" placeholder="Ingrese código de promoción" color="#059669"
                      outlined dense hide-details class="modern-input" />
                  </v-col>
                </v-row>
              </div>

              <!-- Tee Time Slots Section -->
              <div v-if="(typePaid > 0 || !isHotel) && playersNumber != 0 && compTarifas.length >= 1"
                class="tee-slots-section">
                <!-- Section Header -->
                <div class="section-header-bar mb-4">
                  <div class="d-flex align-center">
                    <v-icon color="#0A5C36" class="mr-2" size="28">mdi-clock-time-four-outline</v-icon>
                    <h3 class="section-title" style="font-size: 1.15rem; font-weight: 700;">Selecciona tu horario
                      preferido</h3>
                  </div>

                  <div v-if="isHotel" class="hotel-mode-chip d-none d-sm-inline-flex ml-auto">
                    {{ typePaid === 1 ? 'Golf Incluido' : 'Guest Paid' }}
                  </div>
                </div>

                <!-- Time Validation Error -->
                <div v-if="errors.time" class="modern-error-banner mb-4">
                  <v-icon small color="#EF4444" class="mr-2">mdi-alert-circle</v-icon>
                  Por favor seleccione un horario para continuar
                </div>

                <!-- Time Periods Mockup Pill Toggles -->
                <div class="mockup-pill-toggles mb-5">
                  <button v-for="(tarifas, tarIndx) in sectionsTarifas" :key="`Tarifa-${tarIndx}`" type="button"
                    class="mockup-pill-btn" :class="{ 'mockup-pill-btn--active': section == tarIndx }"
                    @click="section = tarIndx">
                    <v-icon small :color="section == tarIndx ? 'white' : '#94A3B8'" class="mr-1">
                      {{ tarifas.horario.data.Nombre.toLowerCase().includes('twilight') ? 'mdi-weather-night' :
                        'mdi-clock-outline' }}
                    </v-icon>
                    <span>{{ tarifas.horario.data.Nombre }}</span>
                  </button>
                </div>

                <!-- Time Cards Responsive Grid -->
                <v-tabs-items v-model="section" class="transparent"
                  style="background: transparent !important; padding:4px;">
                  <v-tab-item v-for="(tarifas, tarIndx) in sectionsTarifas" :key="`Tarifa-${tarIndx}`"
                    class="transparent">
                    <v-row dense class="tee-grid-row">
                      <template v-for="(time, index) in times.filter((t) => !t.isNoShow)">
                        <v-col v-if="time.tarifaId == tarifas.horario.internalId && time.precio"
                          :key="`Schedule-${index}`" cols="12" md="6" lg="4" class="pa-0 mb-3 px-sm-2">
                          <timeCard v-if="time.tarifaId == tarifas.horario.internalId"
                            v-bind:class="{ cardActive: index === selectedPriceIndex }" :key="index"
                            :showPrice="showPrice" :showPromoPrice="showPromoPrice" :time="time.time"
                            :price="time.precio" :mnd="time.moneda" :priceGlobal="time.precioGlobal"
                            :prefered="time.preferedClass" :players="playersNumber" :reservados="time.personas"
                            :tarifaId="time.tarifaId" :tarifaDocId="tarifas.tarifa.id" :privado="time.privado"
                            :isAdmin="isAdmin" :selectedDate="input_date" @clicked="setSelectedTime" />
                        </v-col>
                      </template>
                    </v-row>
                  </v-tab-item>
                </v-tabs-items>
              </div>

              <!-- Empty / No Times Fallback -->
              <div v-else-if="!loadingTarifas && (compTarifas.length === 0 || playersNumber == 0)"
                class="empty-state-card text-center pa-8 mt-4">
                <v-icon size="56" color="#94A3B8" class="mb-3">mdi-calendar-blank-outline</v-icon>
                <h3 class="text-h6 font-weight-bold" style="color: #0F172A">No hay horarios disponibles</h3>
                <p class="text-body-2" style="color: #64748B">
                  Por favor intente elegir otra fecha o cantidad de jugadores.
                </p>
              </div>
            </div>
          </v-tab-item>

          <!-- ============================================================ -->
          <!-- STEP 3: Player Information & Checkout                         -->
          <!-- ============================================================ -->
          <v-tab-item value="sendPerData" class="transparent">
            <div class="booking-inner-wrap checkout-wrap pt-0">
              <v-row justify="center">
                <v-col cols="12" md="9" lg="7">
                  <!-- Selected Summary Header Capsule -->
                  <div class="checkout-recap-banner mb-4">
                    <div class="d-flex align-center justify-space-between flex-wrap">
                      <div class="d-flex align-center">
                        <v-btn icon color="white" class="mr-2" @click="
                          step = 'dates';
                        forceUp++;
                        ">
                          <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div>
                          <div class="checkout-recap-banner__title">Detalles de Reservación</div>
                          <div class="checkout-recap-banner__sub">
                            {{ input_date.format("ddd, DD MMM YYYY") }} &bull; {{ playersNumber }} Jugador{{
                              playersNumber > 1 ?
                                'es' : '' }} &bull; {{ selectedTime }}
                          </div>
                        </div>
                      </div>

                      <v-btn small rounded color="white" class="change-time-btn mt-2 mt-sm-0" @click="
                        step = 'dates';
                      forceUp++;
                      ">
                        <v-icon small left color="#059669">mdi-pencil</v-icon>
                        Cambiar Hora
                      </v-btn>
                    </div>
                  </div>

                  <!-- Dress Code Notice -->
                  <div v-if="showDressCode" class="dress-code-banner mb-4">
                    <div class="dress-code-icon">
                      <v-icon color="#D97706">mdi-tshirt-crew</v-icon>
                    </div>
                    <div class="dress-code-content">
                      <div class="dress-code-title">Política de Código de Vestimenta</div>
                      <div class="dress-code-text">
                        Se requiere uso de camisa con cuello y calzado de golf adecuado. No se permite mezclilla ni
                        trajes de baño
                        en el campo.
                      </div>
                    </div>
                  </div>

                  <!-- Players Form Error Banner -->
                  <div v-if="errors.players" class="modern-error-banner mb-4">
                    <v-icon small color="#EF4444" class="mr-2">mdi-alert-circle</v-icon>
                    Por favor llene los detalles requeridos de todos los jugadores
                  </div>

                  <!-- Player Details Cards -->
                  <div :key="forceUp">
                    <div v-for="(player, index) in players" :key="index">
                      <v-card v-if="changeGuest || index == 0" class="modern-card player-card mb-4" elevation="0">
                        <!-- Player Card Header -->
                        <div class="player-card__header">
                          <div class="d-flex align-center">
                            <div class="player-card__avatar">
                              <span>{{ index + 1 }}</span>
                            </div>
                            <div>
                              <span class="player-card__title">
                                {{ index === 0 ? 'Titular (Jugador 1)' : `Jugador ${index + 1}` }}
                              </span>
                              <span v-if="index === 0" class="player-card__badge ml-2">Contacto Principal</span>
                            </div>
                          </div>
                        </div>

                        <div class="pa-4 pa-sm-5">
                          <v-row dense>
                            <!-- Full Name -->
                            <v-col cols="12" class="mb-3">
                              <v-text-field v-model="player.name" :rules="rules.required" label="Nombre Completo *"
                                color="#059669" outlined dense required hide-details="auto" class="modern-input" />
                            </v-col>

                            <!-- Email -->
                            <v-col cols="12" sm="6" class="mb-3">
                              <v-text-field v-model="player.email" :rules="rules.email" label="Correo Electrónico *"
                                color="#059669" hide-details="auto" outlined dense required class="modern-input" />
                            </v-col>

                            <!-- Phone -->
                            <v-col cols="12" sm="6" class="mb-3">
                              <v-text-field v-model="player.phone" :rules="rules.phone" type="number"
                                label="Teléfono (Opcional)" color="#059669" hide-details="auto" outlined dense
                                class="modern-input" />
                            </v-col>

                            <!-- Golf Club Rental Addon -->
                            <v-col v-if="showAddons" cols="12" class="mt-1">
                              <div class="addon-toggle-box">
                                <v-switch v-model="player.golfClub" color="#059669" class="mt-0 pt-0" value="true"
                                  hide-details>
                                  <template #label>
                                    <div class="d-flex align-center justify-space-between" style="width: 100%">
                                      <span class="font-weight-medium text-body-2" style="color: #0F172A">
                                        Renta de Bastones
                                      </span>
                                      <span class="addon-price-tag ml-3">
                                        +${{ addOn.golfClub.Precio }} {{ addOn.golfClub.Moneda }}
                                      </span>
                                    </div>
                                  </template>
                                </v-switch>
                              </div>
                            </v-col>
                          </v-row>

                          <!-- Change Other Guests Toggle (on Player 1) -->
                          <div v-if="players.length > 1 && index == 0" class="mt-4 pt-3 border-top-subtle">
                            <v-switch @change="changeAsGuest()" v-model="changeGuest" color="#059669" class="mt-0 pt-0"
                              value="true" hide-details>
                              <template #label>
                                <span class="text-body-2 font-weight-medium" style="color: #334155">
                                  Editar detalles de otros jugadores (opcional)
                                </span>
                              </template>
                            </v-switch>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </div>

                  <!-- Notes / Special Requests -->
                  <v-card class="modern-card pa-4 pa-sm-5 mb-4" elevation="0">
                    <div class="card-heading mb-3">
                      <div class="card-heading__icon">
                        <v-icon color="#059669">mdi-message-text-outline</v-icon>
                      </div>
                      <div>
                        <h3 class="card-heading__title">Peticiones Especiales</h3>
                        <p class="card-heading__sub">Alguna nota para el equipo del campo</p>
                      </div>
                    </div>
                    <v-textarea v-model="nota" placeholder="Agregue requerimientos especiales, preferencias..."
                      color="#059669" outlined rows="2" hide-details class="modern-input" />
                  </v-card>

                  <!-- Order Summary Breakdown Card -->
                  <v-card class="modern-card summary-card mb-8" elevation="0">
                    <div class="summary-card__header pa-4">
                      <div class="d-flex align-center">
                        <v-icon color="#059669" class="mr-2">mdi-receipt-text-outline</v-icon>
                        <h3 class="summary-card__title">Resumen Total</h3>
                      </div>
                    </div>

                    <div class="pa-4 pa-sm-5">
                      <!-- Grand Total Dual Currency Display -->
                      <div class="dual-currency-box">
                        <div class="dual-currency-box__item">
                          <span class="currency-tag">USD</span>
                          <span class="currency-amount">
                            ${{ $uvHelper.formatNumber(grandTotal.total) }}
                          </span>
                        </div>
                        <div class="dual-currency-box__divider"></div>
                        <div class="dual-currency-box__item">
                          <span class="currency-tag">MXN</span>
                          <span class="currency-amount">
                            ${{ $uvHelper.formatNumber(Number(grandTotal.totalPesos).toFixed(2)) }}
                          </span>
                        </div>
                      </div>

                      <!-- Cart Riders Action -->
                      <div v-if="typePaid == 2 && showAddons" class="text-center mt-4">
                        <v-btn color="#059669" outlined rounded small class="rider-modal-btn" @click="toggleModal">
                          <v-icon small left>mdi-cart-plus</v-icon>
                          Añadir Acompañantes
                        </v-btn>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Sticky Bottom Mobile & Desktop Checkout Bar -->
            <footer class="checkout-fixed-footer">
              <div class="checkout-footer-content">
                <div class="checkout-footer-price">
                  <span class="checkout-footer-price__label">Total a Pagar</span>
                  <div class="checkout-footer-price__val">
                    ${{ $uvHelper.formatNumber(grandTotal.total) }} <span class="usd-text">USD</span>
                    <span class="pesos-text">(${{ $uvHelper.formatNumber(Number(grandTotal.totalPesos).toFixed(2)) }}
                      MXN)</span>
                  </div>
                </div>

                <div class="checkout-footer-action">
                  <v-btn v-if="loadingData == false" :disabled="isPlayersInvalid" color="#0A5C36" dark large rounded
                    depressed class="modern-btn-primary px-6 px-sm-8" @click="bookNow()">
                    <span>Confirmar</span>
                    <v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>

                  <v-btn v-else disabled large rounded class="modern-btn-primary px-6 px-sm-8">
                    <v-progress-circular indeterminate size="20" width="2" color="white" class="mr-2" />
                    <span>Procesando...</span>
                  </v-btn>
                </div>
              </div>
              <div class="text-center mt-2 text-caption grey--text font-weight-medium"
                style="font-size: 0.7rem !important; line-height: 1;">
                Powered by <a href="https://pargolfsolutions.com" target="_blank"
                  style="color: #059669; text-decoration: none; font-weight: 700;">ParSoftware</a>
              </div>
            </footer>

            <!-- Cart Riders Dialog -->
            <v-dialog v-model="modalOpen" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="500px">
              <v-card class="modern-card" elevation="0">
                <div class="dialog-header">
                  <span class="dialog-header__title">Acompañantes Extra</span>
                  <v-btn icon color="white" @click="modalOpen = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>

                <v-card-text class="pa-5">
                  <p class="text-body-2 mb-4" style="color: #64748B">
                    Añada acompañantes (no jugadores) a su grupo.
                  </p>

                  <div class="d-flex align-center mb-4">
                    <v-text-field v-model="cartRiderName" label="Nombre del Acompañante" color="#059669" outlined dense
                      hide-details class="modern-input mr-2" />
                    <v-btn color="#059669" dark depressed height="40" rounded @click="addCartRider">
                      <v-icon small left>mdi-plus</v-icon>
                      +{{ addOn.cartRider.Precio }} {{ addOn.cartRider.Moneda }}
                    </v-btn>
                  </div>

                  <!-- Riders List -->
                  <div v-if="cartRiders && cartRiders.length > 0" class="riders-list mb-4">
                    <div v-for="(cartRider, index) in cartRiders" :key="index" class="rider-item">
                      <div class="d-flex align-center">
                        <v-icon small color="#059669" class="mr-2">mdi-account</v-icon>
                        <span class="font-weight-medium" style="color: #0F172A">{{ cartRider.name }}</span>
                      </div>
                      <div class="d-flex align-center">
                        <span class="text-caption font-weight-bold mr-2" style="color: #059669">
                          ${{ addOn.cartRider.Precio }} {{ addOn.cartRider.Moneda }}
                        </span>
                        <v-btn icon x-small @click="deleteCartRider(index)">
                          <v-icon color="#EF4444">mdi-close-circle</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>

                  <v-btn color="#059669" block dark large depressed class="modern-btn-primary"
                    @click="modalOpen = false">
                    Listo
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </div>
    <div class="text-center mt-6 mb-8 text-caption grey--text font-weight-medium">
      Powered by <a href="https://pargolfsolutions.com" target="_blank"
        style="color: #059669; text-decoration: none; font-weight: 700;">ParSoftware</a>
    </div>
  </v-container>
</template>

<script>
//const $ = require('jquery');
//window.$ = $;
import modal from "../MainComponents/modal.vue";
import transportacionEP from "../MainComponents/TransportacionEP.vue";
import moment from "moment";
import Datepicker from "vuejs-datepicker";
import CalendarInput from "../MainComponents/inputs/CalendarInput.vue";
import timeCard from "../MainComponents/timeCardV2.vue";
import bg from '../../assets/img/golf_course_2.jpg';
import FBAxios from "../../helpers/firebaseAxios";
import tarifaMixin from "@/mixins/tarifaMixin";

import rvaMixin from "@/mixins/rvaMixin";

export default {
  name: "booking",
  mixins: [tarifaMixin, rvaMixin],
  components: {
    CalendarInput,
    //Datepicker,
    timeCard,
    //modal,
  },
  data() {
    return {
      step: "dates",
      playerConf: [
        {
          value: "1",
          title: "Player",
          icon: "mdi-account",
        },
        {
          value: "2",
          title: "Players",
          icon: "mdi-account-multiple",
        },
        {
          value: "3",
          title: "Players",
          icon: "mdi-account-group",
        },
        {
          value: "4",
          title: "Players",
          icon: "mdi-account-group",
        },
      ],
      services: [
        "Green Fee",
        "Shared Golf cart",
        "50 practice balls",
        "A bottle of water p/p",
        "Course close at 6:00 pm",
      ],
      openDateInput: false,
      language: null,
      section: 0,
      moneda: "",
      rules: {
        email: [
          (v) => !!v || "El correo es requerido",
          (v) => /.+@.+\..+/.test(v) || "Debe ser un correo válido",
        ],
        phone: [
          (v) =>
            !v || /^\d{10}$/.test(v) || "Debe contener exactamente 10 números",
        ],
        required: [(v) => !!v || "Campo Requerido"],
      },
      forceUp: 1,

      nota: "",
      ConfigRva: null,
      clienDataModal: false,
      MainDepartamento: "",
      presentAlert: false,
      loadingTarifas: false,
      tranindx: 1,
      transModal: false,
      input_date: moment(),
      actualDate: moment(),
      roomNumber: "",
      isAdmin: false,
      transportation: false,
      loadingData: false,
      private_reservation: false,
      isPreferedTime: false,
      Session: "",
      ApiToken: "",
      TotalTrans: 0,
      RowId: "",
      DuracionViaje: "",
      rawDuracionViaje: "",
      HtlPickup: "",
      PCGCPickup: "",
      changeGuest: false,
      promotionalCode: "",
      selectedTarifa: "",
      compIndex: 0,
      actualListener: "",
      prefered: true,
      RsvaTransId: "",
      // Confirm:"",
      modalOpen: false,
      selectedPriceIndex: "",
      selectedTime: "",
      playersNumber: "",
      cartRiderName: "",
      addOn: { cartRider: "", golfClub: "", transport: "" },
      hoyos: "",
      tarifarioWebData: null,
      players: [
        {
          name: "",
          email: "",
          phone: "",
          golfClub: false,
          side: "right",
          genero: "Man",
          shaft: "regular",
        },
      ],
      tiposJuego: [],
      temporadas: [],
      tarifas: [],
      tarifaGlobal: [],
      times: [],
      reservas: [],
      cartRiders: [],
      daysRules: [],
      Horarios: [],
      Mercados: [],
      Segmentos: [],
      comboPlayers: [
        { value: "1", text: "1 Player" },
        { value: "2", text: "2 Player" },
        { value: "3", text: "3 Player" },
        { value: "4", text: "4 Player" },
      ],
      initTime: new moment("06:00", "HH:mm"),
      endTime: new moment("18:00", "HH:mm"),
      configTime: {},
      intervalo: 10,
      errors: {
        time: false,
        players: false,
        code: false,
      },
      highlighted: {
        dates: [
          // Highlight an array of dates
          new Date(moment()),
          //new Date(2020, 0, 17),
          //new Date(2020, 0, 18)
        ],
      },
    };
  },
  props: ["isHotel", "isWeb"],
  methods: {
    init() {
      this.getTiposJuego()
        .then(() => {
          return this.getTemporadas();
        })
        .then(async () => {
          await this.getHorarios();
          this.getAddons();
          this.getDaysRules();
        });
    },
    toggleModal() {
      this.modalOpen = !this.modalOpen;
    },
    addCartRider() {
      if (this.cartRiderName !== "") {
        this.cartRiders.push({ name: this.cartRiderName });
        this.cartRiderName = "";
        if (this.transportation) {
          this.getTransportationData();
        }
      } else {
        this.$swal({
          icon: "error",
          title: "Cart Rider Name is required",
          text: "",
        });
      }
    },
    deleteCartRider(index) {
      this.cartRiders.splice(index, 1);
      //this.getTransportationData();
    },
    changePlayersNumber() {
      this.presentAlert = false;
      this.resetSelect();
      //this.players= [{name:'',email:'',phone:'',golfClub:false,side:"right",genero:'Man',shaft:'regular'}];
      if (this.players.length < this.playersNumber) {
        var i;
        for (i = this.players.length; i < this.playersNumber; i++) {
          this.players.push({
            name: "",
            email: "",
            phone: "",
            golfClub: false,
            side: "right",
            genero: "Man",
            shaft: "regular",
          });
        }
      } else if (this.players.length > this.playersNumber) {
        for (i = this.playersNumber; i < this.players.length; i) {
          this.players.pop();
        }
      }
      if (this.transportation) {
        this.getTransportationData();
      }
    },
    changeTrans(value) {
      //this.transportation = value;
      if (value == true) {
        this.transModal = true;
      } else {
        /*if(this.selectedPriceIndex === ""){
                this.$swal(
                    'Selecciona un horario',
                    'Selecciona un horario de juego antes de solicitar transportacion ',
                    'error'
                )

                document.getElementsByName('trans').forEach((element =>{
                    element.checked = false;
                }))
        }else if(this.transportation && this.selectedPriceIndex !== ""){
            if(this.isHotel){
                this.getTransportationData();
            }
            
        }*/
        this.Session = "";
        this.ApiToken = "";
        this.TotalTrans = 0;
        this.RowId = "";
        this.RsvaId = "";
        this.DuracionViaje = "";
        this.HtlPickup = "";
        this.PCGCPickup = "";
        //this.Confirm ="";
        this.transportation = value;
        this.tranindx++;
        /*this.$swal(
                'No se transportara a los jugadores',
                'La transportacion al campo de golf  no sera incluida en esta reserva ',
                'error'
            )*/
      }
    },
    changeHoyos() {
      this.resetSelect();
      (this.selectedTime = ""), (this.selectedPrice = ""), this.compIndex++;
      this.getTarifas();
    },
    changeAsGuest() {
      var $this = this;
      if (!this.changeGuest) {
        let pNumber = 1;
        this.players.forEach((element, index) => {
          if (index !== 0 && element.name === "") {
            element.name = this.players[0].name ?? null;
            element.email = this.players[0].email ?? null;
            element.phone = this.players[0].phone ?? null;
            pNumber++;
          }
        });
      } else {
        this.players.forEach(function (element, index) {
          if (index != 0) {
            element.name = "";
            element.email = "";
            element.phone = "";
          }
        });
      }
    },
    showPrefered() {
      this.resetSelect();
      if (this.prefered) {
        /* this.times.forEach((element)=>{
                   element.prefered = false;
           });*/
        this.times.forEach((element, index) => {
          if (element.personas > 0 && element.prefered == true) {
            this.times[index - 1].preferedClass = true;
            element.preferedClass = true;
            this.times[index + 1].preferedClass = true;
          }
        });
      } else if (this.prefered === false) {
        this.times.forEach((element) => {
          element.preferedClass = true;
        });
      }
    },
    setSelectedTime(key, time, idTarifa, mnd, prefered) {
      //alert(key + ' ' + time + ' ' + total);

      this.selectedPriceIndex = key;
      this.selectedTime = time;
      this.moneda = mnd;
      this.selectedTarifa = idTarifa;
      this.isPreferedTime = prefered;
      this.clienDataModal = true;
      this.step = "sendPerData";
      this.changeAsGuest();
      this.$nextTick(() => {
        this.changeGuest = null;
        //document.getElementById("dataClient").scrollIntoView();
      });
    },
    selectDate(arg) {
      //let oldDate= this.input_date.format('YYYY-MM-DD');
      //this.killListener(oldDate);
      this.tarifas = []
      if (this.playersNumber == 0) {
        this.presentAlert = true;
      }
      this.loadingTarifas = true;
      this.input_date = moment(arg);
      this.resetSelect();
      (this.selectedTime = ""), (this.selectedPrice = ""), this.getTarifas();
      this.openDateInput = false;
    },
    setInitialDate() {
      this.input_date.format("YYYY-MM-DD");
      let aux = false;
      this.daysRules.forEach((element) => {
        if (element.data.Abre && element.data.Cierra) {
          if (this.input_date.format("YYYY-MM-DD") === element.date) {
            this.initTime = new moment(element.data.Abre, "HH:mm:ss");
            this.endTime = new moment(element.data.Cierra, "HH:mm:ss");
            aux = true;
          }
        }
      });
      if (!aux) {
        this.initTime = this.configTime.initTime;
        this.endTime = this.configTime.endTime;
      }
    },
    killListener(date) {
      this.$store.getters.getFirebaseApp.database.ref("Reservas/" + date).off();
    },
    selectPaid(value) {
      this.$store.commit("setTypePaid", value);
      this.getTarifas();
    },
    resetSelect() {
      this.selectedPriceIndex = "";
      this.isPreferedTime = false;
      document.getElementsByName("trans").forEach((element) => {
        element.checked = false;
      });
      this.$store.commit("setPriceSelected", 0);
      this.$store.commit("setMndSelected", "");
    },

    async getTarifas() {
      let idTemporada = "";
      let temporadasAplicables = [];
      this.temporadas.forEach((element) => {
        if (
          moment(element.FechaInicio).format("YYYY-MM-DD") <=
          this.input_date.format("YYYY-MM-DD") &&
          moment(element.FechaTermino).format("YYYY-MM-DD") >=
          this.input_date.format("YYYY-MM-DD")
        ) {
          idTemporada = element.id;

          temporadasAplicables.push(element.id);
        }
      });
      let TarifasList = [];

      let taskTarifas = temporadasAplicables.map(async (id) => {
        let tarifas = await this.getMixTarifasByTarifarioParse(
          "WEB",
          id,
          this.MainDepartamento,
          this.Horarios,
          this.temporadas,
          this.tiposJuego,
          this.input_date.day()
        );
        if (tarifas[0]) {
          this.tarifas = [tarifas[0]];
        }

        let tarifasGlobal = await this.getMixTarifasByTarifarioParse(
          "GLOBAL",
          id,
          this.MainDepartamento,
          this.Horarios,
          this.temporadas,
          this.tiposJuego,
          this.input_date.day()
        );
        tarifasGlobal.forEach((tarifa) => {
          if (tarifa.Subcategoria.includes(this.tarifas[0].Subcategoria[0])) {
            this.tarifaGlobal = [tarifa];
          }
        });
      });

      await Promise.all(taskTarifas).then(() => { });

      /*let tarifas = await this.getMixTarifasByTarifarioParse(
        "WEB",
        idTemporada,
        this.MainDepartamento,
        this.Horarios,
        this.temporadas,
        this.tiposJuego,
        this.input_date.day()
      );
      this.tarifas = [tarifas[0]];

      let tarifasGlobal = await this.getMixTarifasByTarifarioParse(
        "GLOBAL",
        idTemporada,
        this.MainDepartamento,
        this.Horarios,
        this.temporadas,
        this.tiposJuego,
        this.input_date.day()
      );
      tarifasGlobal.forEach((tarifa) => {
        if (tarifa.Subcategoria.includes(this.tarifas[0].Subcategoria[0])) {
          this.tarifaGlobal = [tarifa];
        }
      });*/
      this.tarifarioWebData = await this.getTarifarioById("Golf", "WEB");
      await this.getReservas();
      this.buildTimesData();
      this.loadingTarifas = false;

      /*
       

        if(this.isWeb){
            arrayAccess.push('Web');
        }
        
        this.$store.getters.getFirebaseApp.firebase.collection("Golf_Tarifas")
        //.where('TipoJuego.Id','==',this.hoyos)
        .where('Temporadas',"array-contains",idTemporada)
        .where("Activo", "==", true)
        //.where('Accesos','array-contains-any',arrayAccess)
        .where("Tenant","==",this.$store.getters.getFirebaseApp.tenant).get().then(snapshot=>snapshot.docs.map(doc=>{return {Id:doc.ref.id,...doc.data()}})).then(tarifas=>{
            
            let task = tarifas.map(async(tarifa)=>{
                let tarifaTemporda = await this.$store.getters.getFirebaseApp.firebasedoc(`Golf_Tarifas/${tarifa.Id}/Temporadas/${this.selectedTemporada}`).where("Tenant","==",this.$store.getters.getFirebaseApp.tenant).get();
                tarifa.preciosTemporada =  {Id:tarifaTemporda.ref.id,...tarifaTemporda.data()};
                return tarifa;
            });
         
            return Promise.all(task);
            
    
        }).then((tarifas)=>{
            
           this.tarifas =[];
            var aux = 1;
            let tarifasArray = [];
            tarifas.forEach((element)=>{
               
                element.id = aux;
                
               
                let arrayHorarios =  Object.entries(element.preciosTemporada.Horarios);
                
                arrayHorarios.forEach((horario)=>{
                    
                    let accesso = '';
 
                    if(element.Id === "WEB"){
                        accesso = "Web";
                    }else if(element.Id === "GLOBAL"){
                        accesso = "Global";
                    }else{
                         accesso = element.Accesos[0];
                    }

                    let tipo_juego = Object.entries(horario[1].TiposJuego);

                    tipo_juego.forEach((juego)=>{
                        let tarifaObj = {}
                   
                        tarifaObj.id = aux;
                        aux++;
                        tarifaObj.Id = horario[0];
                        tarifaObj.Accesos = [accesso];
                        tarifaObj.Temporada= {}
                        tarifaObj.Temporada.Nombre = element.Nombre
                        tarifaObj.EsPrecioHotel = element.EsPrecioHotel;
                        tarifaObj.Horario = horario[1];
                        tarifaObj.TipoJuego = {};
                        tarifaObj.TipoJuego.Id =juego[0];
                        tarifaObj.TipoJuego.Nombre = juego[1].Nombre;
                        tarifaObj.Moneda = juego[1].Moneda;
                        tarifaObj.Precio =juego[1].Precio;
                        tarifasArray.push(tarifaObj);

                    });
                   
                    
                    
                });
            });

            tarifasArray.sort(function compare( a, b ) {
                return moment(a.Horario.HoraInicio,'HH:mm') - moment(b.Horario.HoraInicio,'HH:mm');
            });

            //this.tarifas = tarifasArray;

            this.$uvHelper.log(tarifasArray);
           

           

           
        });*/
    },
    async getHorarios() {
      //const data = await this.$store.getters.getFirebaseApp.database.ref("Golf/Tarifas/Horarios").once("value");
      const data = await this.$store.getters.getFirebaseApp.database
        .ref("RVA/Tarifas/" + this.MainDepartamento + "/Horarios")
        .once("value");

      if (!data.exists()) {
        return false;
      }
      let arrayHorarios = Object.entries(data.val());
      var $this = this;
      arrayHorarios.forEach(function (element) {
        $this.Horarios.push({ id: element[0], ...element[1] });
      });
    },
    async testRealTime() {
      const data = await this.$store.getters.getFirebaseApp.database
        .ref("Configuracion/Reservas/EmailCCO")
        .once("value");
      this.$uvHelper.log(data.val());
    },
    async getTransportationData() {
      /* 
        Obiene la informacion de la transportacion para la reserva
        */
      let data = {
        PaxNumber: this.players.length + this.cartRiders.length,
        hotelCode: this.$route.params.user.tokenResult.claims.Code,
        Prefered: this.isPreferedTime,
        Fecha:
          this.input_date.format("YYYY-MM-DD") +
          "_" +
          moment(this.selectedTime, "HH:mm").format("HH:mm:ss"),
      };
      this.loadingData = true;
      //var getTransportation = this.$store.getters.getFirebaseApp.fba.call('Golf-Reservas-transportationAvailability');
      return await this.$store.getters.getFirebaseApp.fba
        .call("Golf-Reservas-transportationAvailability", data, {})
        .then((result) => {
          this.Session = result.data.data.Session;
          this.ApiToken = result.data.data.ApiToken;
          this.TotalTrans =
            this.typePaid === 1
              ? this.playersNumber >= 4
                ? 0
                : this.addOn.transport.Precio
              : this.playersNumber >= 2
                ? 0
                : this.addOn.transport.Precio;
          this.RowId = result.data.data.RowId;
          this.RsvaTransId = result.data.data.RsvaTransId;
          this.rawDuracionViaje = result.data.data.DuracionViaje;
          this.DuracionViaje = moment(
            result.data.data.DuracionViaje,
            "HH:mm:ss"
          ).format("HH [Hrs] : mm [Min].");
          this.HtlPickup = moment(
            result.data.data.HtlPickup,
            "YYYY-MM-DD_HH:mm:ss"
          ).format(" DD-MMM-YYYY HH:mm ");
          this.PCGCPickup = moment(
            result.data.data.PCGCPickup,
            "YYYY-MM-DD_HH:mm:ss"
          ).format(" DD-MMM-YYYY HH:mm ");
          //this.Confirm= result.data.data.Confirm;
          this.loadingData = false;
        })
        .catch((error) => {
          this.$uvHelper.log(error);
        });
    },
    async getTiposJuego() {
      const data = await this.$store.getters.getFirebaseApp.database
        .ref("RVA/Tarifas/" + this.MainDepartamento + "/Modalidades")
        .once("value");
      let arrayTipos = Object.entries(data.val());
      var $this = this;
      arrayTipos.forEach((element) => {
        $this.tiposJuego.push({
          id: element[0],
          codigo: element[1]["Codigo"],
          nombre: element[1]["Nombre"],
        });
        if (element[1]["Codigo"] == "18HOLES") {
          this.hoyos = element[0];
        }
      });
    },
    async getTemporadas() {
      /*
         Obtiene las temporadas existentes en la db
        */
      const data = await this.$store.getters.getFirebaseApp.database
        .ref("RVA/Tarifas/" + this.MainDepartamento + "/Temporadas")
        .once("value");
      if (!data.exists()) {
        return false;
      }
      let arrayTarifas = Object.entries(data.val());
      arrayTarifas.forEach((element) => {
        if (element[1].Activo) {
          this.temporadas.push({ id: element[0], ...element[1] });
        }
      });
    },
    async getAddons() {
      /*
         Obtiene las temporadas existentes en la db
        */
      let addons = this.$store.getters.getRVACatalogo;
      /*const dataExt = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/ExtraRidder").once("value");
        let arrayExt =  dataExt.val();*/
      this.addOn.cartRider = addons.GOLFRIDDER; //arrayExt;

      /*const dataGb = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/GolfClub").once("value");
        let arrayGb =  dataGb.val();*/
      this.addOn.golfClub = addons.GOLFCLUB; //arrayGb;

      /*const dataTr = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/Transport").once("value");
        const arrayTr = await dataTr.val();*/
      this.addOn.transport = addons.TRANSPORT;
      /*arrayTarifas.forEach((element)=>{
           this.temporadas.push({id:element[0],...element[1]}); 
        });

        const dataGb = await this.$store.getters.getFirebaseApp.database.ref("Addons/GolfClub").once("value");
        let arrayTarifas =  Object.entries(data.val());
        arrayTarifas.forEach((element)=>{
           this.temporadas.push({id:element[0],...element[1]}); 
        });*/
    },
    async getReservas() {
      /*
            Se obtiene las disponibilidad por dia y la cantidad de personas reservadas en esos dias
        */
      let date = this.input_date.format("YYYY-MM-DD");
      this.killListener(this.actualListener);

      this.$store.getters.getFirebaseApp.database
        .ref("RVA/Reservas/" + this.MainDepartamento + "/" + date)
        .on("value", (snapshot) => {
          if (snapshot.exists()) {
            this.reservas = [];

            let arrayReservas = snapshot.val();
            arrayReservas = Object.entries(arrayReservas[1]);
            arrayReservas.forEach((element) => {
              this.reservas.push({ time: element[0], ...element[1] });
            });
          } else {
            this.reservas = [];
          }

          this.buildTimesData();
          this.showPrefered();
        });

      this.actualListener = date;
    },
    async getDaysRules() {
      this.$store.getters.getFirebaseApp.database
        .ref("RVA/Configuracion/" + this.MainDepartamento + "/Calendario")
        .on("value", (snapshot) => {
          if (snapshot.exists()) {
            this.daysRules = [];

            if (snapshot.val().Fechas_Especiales) {
              let arrayCalendar = Object.entries(
                snapshot.val().Fechas_Especiales
              );
              arrayCalendar.forEach((element) => {
                this.daysRules.push({ date: element[0], data: element[1] });
              });

              //ordena las reglas por fecha
              this.daysRules.sort(function compare(a, b) {
                return a.date - b.date;
              });
              //si la fecha inicial esta entre las reglas como un dia cerrado aumenta un dia
              this.daysRules.forEach((element) => {
                if (
                  element.date == this.input_date.format("YYYY-MM-DD") &&
                  element.data == false
                ) {
                  this.input_date.add(1, "days");
                }
              });
              // si es un lunes, verificar si en las reglas abre , si no aumentar un dia
              /*if (this.input_date.weekday() === 1) {
                let abre = false;
                this.daysRules.forEach((element) => {
                  if (
                    element.date == this.input_date.format("YYYY-MM-DD") &&
                    element.data == true
                  ) {
                    abre = true;
                  }
                });

                if (abre == false) {
                  this.input_date.add(1, "days");
                }
              }*/

              this.configTime.initTime = new moment(
                snapshot.val().Hora_Apertura,
                "HH:mm:ss"
              );
              this.configTime.endTime = new moment(
                snapshot.val().Hora_Cierre,
                "HH:mm:ss"
              );
            } else {
              this.configTime.initTime = new moment(
                snapshot.val().Hora_Apertura,
                "HH:mm:ss"
              );
              this.configTime.endTime = new moment(
                snapshot.val().Hora_Cierre,
                "HH:mm:ss"
              );
              this.daysRules = [];
            }
          } else {
            this.daysRules = [];
          }
          this.getTarifas();
        });
    },
    buildTimesData() {
      /*
            Genera el array conteniendo cada horario disponible para reserva en un intervalo de 10 minutos desde la hora inicial y la hora final
        */
      this.initTime = "";
      this.endTime = "";
      this.setInitialDate();
      let initTim = new moment(this.initTime.format("HH:mm"), "HH:mm");
      let endTime = new moment(this.endTime.format("HH:mm"), "HH:mm");

      let auxDate = initTim;
      this.times = [];

      var i;
      /*para cada uno de los horarios armados se asignan los datos necesarios*/
      for (i = 0; auxDate.format("HH:mm") <= endTime.format("HH:mm"); i++) {
        var idTarifa;
        let precio = null;
        var moneda;
        var idTarifaGlobal = "";
        var precioGlobal = "";
        let personas = 0;
        let privado = false;
        let prefered = false;
        /* Se asigna la tarifa y el precio */
        this.tarifas.forEach((tarifa) => {
          tarifa.arrayPrecios.forEach((modalidad) => {
            modalidad.horarios.forEach((horario) => {
              if (modalidad.id == this.hoyos) {
                if (
                  horario.data.HoraInicio <= auxDate.format("HH:mm:ss") &&
                  horario.data.HoraTermino >= auxDate.format("HH:mm:ss")
                ) {
                  idTarifa = horario.internalId;
                  precio = horario.PcoVen;
                  moneda = horario.Moneda;
                }
              }
            });
          });
        });
        /*Se asigna la tarifa global y el precio global*/
        if (this.tarifas.length > 0) {
          this.tarifaGlobal.forEach((tarifa) => {
            tarifa.arrayPrecios.forEach((modalidad) => {
              modalidad.horarios.forEach((horario) => {
                if (modalidad.id == this.hoyos) {
                  if (
                    horario.data.HoraInicio <= auxDate.format("HH:mm:ss") &&
                    horario.data.HoraTermino >= auxDate.format("HH:mm:ss")
                  ) {
                    idTarifaGlobal = horario.internalId;
                    precioGlobal = horario.PcoVen;
                  }
                }
              });
            });
          });
        }


        /*en caso de que tengan reservas en ese Horario y dia seleccionado se agregan a los datos*/
        this.reservas.forEach((element) => {
          let time = new moment(element.time, "HH:mm:ss");
          if (time.format("HH:mm") == auxDate.format("HH:mm")) {
            let rvs = element.Rvs ? element.Rvs : {};
            let rvsArray = Object.entries(rvs);
            rvsArray.forEach((res) => {
              if (res[1].Pys) {
                res[1].Pys.forEach((ply) => {
                  if (ply.E == "R" || ply.E == "P") {
                    personas++;
                  }
                });
              }
            });
            if (this.isHotel) {
              prefered = element.Prefered ? true : false;
            } else {
              prefered = false;
            }
            privado = element.Privado ? element.Privado : false;
          }
        });
        if (!precio) {
          privado = true;
        }

        const isNoShow =
          this.actualDate.format("YYYY-MM-DD HH:mm") >
          moment(
            this.input_date.format("YYYY-MM-DD") + auxDate.format("HH:mm"),
            "YYYY-MM-DD HH:mm"
          ).format("YYYY-MM-DD HH:mm");

        /* se agregan al array*/
        this.times.push({
          time: auxDate.format("HH:mm"),
          tarifaId: idTarifa,
          tarifaIdGlobal: idTarifaGlobal,
          precioGlobal: precioGlobal,
          precio: precio,
          moneda: moneda,
          personas: personas,
          privado: privado,
          prefered: prefered,
          isNoShow: isNoShow,
        });
        auxDate = auxDate.add(10, "minutes");
      }
    },
    validation() {
      var valid = true;

      if (this.selectedPriceIndex === "") {
        valid == false;
        this.errors.time = true;
      } else {
        this.errors.time = false;
      }
      let aux = 0;
      this.players.forEach((element) => {
        if (element.name == "" || element.email == "") {
          aux++;
        }
      });

      if (aux > 0) {
        valid = false;
        this.errors.players = true;
      } else {
        this.errors.players = false;
      }

      if (this.typePaid === 1 && this.promotionalCode === "") {
        valid = false;
        this.errors.code = true;
      } else {
        this.errors.code = false;
      }

      return valid;
    },
    buildReservationData() {
      this.players.forEach((element, index) => {
        let tarifa = this.$uvHelper.getById(
          this.tarifas,
          "id",
          this.selectedTarifa
        );
        let tarifaName = "";
        let categoriaId = null;
        let categoriaName = null;

        tarifa.Subcategoria.forEach((element) => {
          categoriaId = element;
          categoriaName = this.$uvHelper.getById(
            this.subcategorias,
            "id",
            element
          ).Nombre;
          tarifaName +=
            this.$uvHelper.getById(this.subcategorias, "id", element).Nombre +
            " ";
        });
        tarifa.cateTarifa = {
          id: categoriaId,
          nombre: categoriaName,
        };
        tarifa.arrayPrecios.forEach((modalidad) => {
          modalidad.horarios.forEach((horario) => {
            if (modalidad.id == this.hoyos) {
              if (
                horario.data.HoraInicio <=
                moment(this.selectedTime, "HH:mm").format("HH:mm:ss") &&
                horario.data.HoraTermino >=
                moment(this.selectedTime, "HH:mm").format("HH:mm:ss")
              ) {


                const segmentoId = tarifa?.Segmento || this.tarifarioWebData?.Segmento || null;
                const mercadoId = tarifa?.Mercado || this.tarifarioWebData?.Mercado || null;

                const segmentoTarifaData = (segmentoId && Array.isArray(this.Segmentos))
                  ? (this.Segmentos.find((s) => s.id === segmentoId || s.Id === segmentoId) || null)
                  : null;
                const mercadoData = (mercadoId && Array.isArray(this.Mercados))
                  ? (this.Mercados.find((m) => m.id === mercadoId || m.Id === mercadoId) || null)
                  : null;
                this.players[index].TarifaSelected = {
                  Tarifa: tarifa,
                  Nombre: "WEB",
                  Turista: tarifa?.Turista || false,
                  Color: tarifa?.Color || null,
                  TarifaId: tarifa?.id,
                  Horario: horario,
                  horarioNombre: horario?.data?.Nombre,
                  Precio: horario?.PcoVen,
                  PrecioCon: horario?.PcoCon,
                  Moneda: horario?.Moneda,
                  Seg: segmentoId || null,
                  SegN: segmentoTarifaData?.nm || null,
                  Mer: mercadoId || null,
                  MerN: mercadoData?.nm || null,
                };
                this.$forceUpdate();
              }
            }
          });
        });
      });

      let resData = this.buildRVAData({
        Tipo: "Golf",
        Cliente: this.players[0],
        Players: this.players,
        Familia: this.ConfigRva?.Contabilidad?.Familia,
        Estatus: "Confirmed",
        Privado: false,
        FechaInicial:
          this.input_date.format("YYYY-MM-DD") +
          "_" +
          moment(this.selectedTime, "HH:mm").format("HH:mm:ss"),
        Total: this.grandTotal,
        Prepagado: this.typePaid === 1 ? true : false,
        Notas: this.nota,
        Departamento: this.MainDepartamento,
        Tags: ["GOLF"],
        Creador: this.isWeb ? "Web" : "ProShop",

        Modalidad: this.$uvHelper.getById(this.tiposJuego, "id", this.hoyos),
        CartRiders: this.cartRiders,
        Addons: this.Addons,
        Transportacion: null,
        Mayorista: null,
      });

      return resData;
    },
    async getConfig() {
      const conf = await this.$store.getters.getFirebaseApp.database
        .ref("RVA/Configuracion/Golf")
        .once("value");
      let resultConf = conf.val();
      this.ConfigRva = resultConf;
    },
    async bookNow() {
      if (!this.changeGuest) await this.changeAsGuest();
      const form = this.$refs.form;
      if (form.validate()) {
        let reservationData = this.buildReservationData();

        this.$swal({
          title: "Book now?",
          text: "You will not be able to modify your reservation later",
          icon: "warning",
          allowOutsideClick: false,
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Book now!",
          showLoaderOnConfirm: true,
          preConfirm: async () => {
            //var addReserva = this.$store.getters.getFirebaseApp.fba.call('RVA-Reservas-crearReserva');
            return await this.$store.getters.getFirebaseApp.fba
              .call("RVA-Reservas-crearReserva", reservationData)
              .then(function (result) {
                return result;
              })
              .catch((error) => {
                this.$swal.showValidationMessage(`Request failed: ${error}`);
              });
          },
        }).then((result) => {
          if (result.value) {
            if (result.value.data.ok) {
              let transReservation = "";
              if (result.value.data.data.Transportacion) {
                transReservation =
                  "Transportation number:" +
                  result.value.data.data.Transportacion.Confirmacion;
              }

              this.$swal({
                title: "Reserved",
                text:
                  "Your reservation is done, we send an email with reservation info.    Reservation No. " +
                  result.value.data.data.Folio +
                  " " +
                  transReservation,
                icon: "success",
              }).then(() => {
                if (this.isWeb) {
                  if (this.$store.getters.getFirebaseApp.tenant == "PCUN") {
                    location.href = "https://www.puertocancun.com/";
                  }
                } else {
                  this.$emit("changeIndex");
                }
              });

              this.$emit("changeIndex");
            } else {
              this.$swal(
                "Error saving reservation",
                result.value.data.error,
                "error"
              );
            }
          }
        });
      }
    },
    setTransData(data) {
      this.Session = data.Session;
      this.ApiToken = data.ApiToken;
      this.TotalTrans = data.TotalTrans;
      this.RowId = data.RowId;
      this.RsvaTransId = data.RsvaTransId;
      this.rawDuracionViaje = data.DuracionViaje;
      this.DuracionViaje = moment(data.DuracionViaje, "HH:mm:ss").format(
        "HH [Hrs] : mm [Min]."
      );
      if (data.typeTransportation == 1) {
        this.HtlPickup =
          data.HtlPickup.format(" DD-MMM-YYYY ") +
          moment(data.htime).format("HH [Hrs] : mm [Min].");
        this.PCGCPickup =
          data.PCGCPickup.format(" DD-MMM-YYYY ") +
          moment(data.gtime).format("HH [Hrs] : mm [Min].");
      } else if (data.typeTransportation == 2) {
        this.HtlPickup =
          data.HtlPickup.format(" DD-MMM-YYYY ") +
          moment(data.htime).format("HH [Hrs] : mm [Min].");
        this.PCGCPickup = "NA";
      } else if (data.typeTransportation == 3) {
        this.HtlPickup = "NA";
        this.PCGCPickup =
          data.PCGCPickup.format(" DD-MMM-YYYY ") +
          moment(data.gtime).format("HH [Hrs] : mm [Min].");
      } else if (data.typeTransportation == 4) {
        this.HtlPickup =
          data.HtlPickup.format(" DD-MMM-YYYY ") +
          data.HtlPickup.format("HH [Hrs] : mm [Min].");
        this.PCGCPickup =
          data.PCGCPickup.format(" DD-MMM-YYYY ") +
          data.PCGCPickup.format("HH [Hrs] : mm [Min].");
      }

      this.transportation = true;
      this.transModal = false;
    },
  },
  computed: {
    isTnto() {
      return this.$store.getters.getFirebaseApp.tenant == "TNTO";
    },
    showAddons() {
      if (this.$store.getters.getFirebaseApp.tenant == "PCUN") {
        return true;
      }
      return false;
    },
    showPromoPrice() {
      if (this.$store.getters.getFirebaseApp.tenant == "PCUN") {
        return true;
      }
      return false;
    },
    showDressCode() {
      if (this.$store.getters.getFirebaseApp.tenant == "PCUN") {
        return true;
      }
      return false;
    },
    showPrice() {
      return true;
    },
    Addons() {
      /*
         Obtiene las temporadas existentes en la this.$store.getters.getFirebaseApp.database
        */
      let AddOn = {};
      let addons = this.$store.getters.getRVACatalogo;
      /*const dataExt = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/ExtraRidder").once("value");
        let arrayExt =  dataExt.val();*/
      AddOn.cartRider = addons.GOLFRIDDER; //arrayExt;

      /*const dataGb = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/GolfClub").once("value");
        let arrayGb =  dataGb.val();*/
      AddOn.golfClub = addons.GOLFCLUB; //arrayGb;

      /*const dataTr = await this.$store.getters.getFirebaseApp.database.ref("Golf/Addons/Transport").once("value");
        const arrayTr = await dataTr.val();*/
      AddOn.transport = addons.TRANSPORT;
      AddOn.roundGolf = addons.GOLFROUND;
      /*arrayTarifas.forEach((element)=>{
           this.temporadas.push({id:element[0],...element[1]}); 
        });
        
        const dataGb = await this.$store.getters.getFirebaseApp.database.ref("Addons/GolfClub").once("value");
        let arrayTarifas =  Object.entries(data.val());
        arrayTarifas.forEach((element)=>{
           this.temporadas.push({id:element[0],...element[1]}); 
        });*/
      return AddOn;
    },
    typePaid() {
      return this.$store.getters.getTypePaid;
    },
    compTarifas() {
      let compTarifas = [];
      this.tarifas.forEach((tarifa) => {
        let HorarioAplicable = [];
        tarifa.arrayPrecios.forEach((modalidad) => {
          modalidad.horarios.forEach((horario) => {
            if (modalidad.id == this.hoyos) {
              if (this.showAll) {
                this.$uvHelper.log("mostrar todos");
                HorarioAplicable.push(horario);
              } else {
                if (
                  moment(
                    this.input_date.format("YYYY-MM-DD") +
                    horario.data.HoraTermino,
                    "YYYY-MM-DD HH:mm:ss"
                  ).format("YYYY-MM-DD HH:mm:ss") >
                  this.actualDate.format("YYYY-MM-DD HH:mm:ss")
                ) {
                  if (
                    moment(
                      this.input_date.format("YYYY-MM-DD") +
                      this.initTime.format("HH:mm:ss"),
                      "YYYY-MM-DD HH:mm:ss"
                    ).format("YYYY-MM-DD HH:mm:ss") <
                    moment(
                      this.input_date.format("YYYY-MM-DD") +
                      horario.data.HoraTermino,
                      "YYYY-MM-DD HH:mm:ss"
                    ).format("YYYY-MM-DD HH:mm:ss")
                  ) {
                    HorarioAplicable.push(horario);
                  }
                }
              }
            }
          });
        });
        tarifa.HorariosVisibles = HorarioAplicable;
        compTarifas.push(tarifa);
      });
      return compTarifas;
    },
    tiposCambio() {
      let cambio = this.$store.getters.getTiposCambios;
      return cambio[0];
    },
    grandTotal() {
      /* calcula el total de la reserva, segun el tipo de pago y los items seleccionados */
      let total = 0;
      let totalPesos = 0;
      let Moneda = this.$store.getters.getMndSelected;
      let montoTarifa =
        this.$store.getters.getPriceSelected != ""
          ? this.$store.getters.getPriceSelected
          : 0;
      total += parseInt(this.TotalTrans);
      if (Moneda == "USD") {
        total += montoTarifa;
        totalPesos += montoTarifa * this.tiposCambio.TC.USD;
      } else if (Moneda == "MXN") {
        total += montoTarifa / this.tiposCambio.TC.USD;
        totalPesos += montoTarifa;
      }
      if (this.typePaid === 1) {
        total = 0;
        totalPesos = 0;
      }
      this.players.forEach((element) => {
        if (element.golfClub) {
          if (this.addOn.golfClub.Moneda == "USD") {
            total += this.addOn.golfClub.Precio;
            totalPesos += this.addOn.golfClub.Precio * this.tiposCambio.TC.USD;
          } else {
            total += this.addOn.golfClub.Precio / this.tiposCambio.TC.USD;
            totalPesos += this.addOn.golfClub.Precio;
          }
        }
      });
      this.cartRiders.forEach(() => {
        if (this.addOn.cartRider.Moneda == "USD") {
          total += this.addOn.cartRider.Precio;
          totalPesos += this.addOn.cartRider.Precio * this.tiposCambio.TC.USD;
        } else {
          total += this.addOn.cartRider.Precio / this.tiposCambio.TC.USD;
          totalPesos += this.addOn.cartRider.Precio;
        }
      });

      return { total: total, totalPesos: totalPesos };
    },
    computedStep() {
      let step = 3;
      let stepExtraInfo = 4;
      if (this.isHotel) {
        if (this.typePaid == 2) {
          step = 4;
          stepExtraInfo = 5;
        } else if (this.typePaid == 1) {
          step = 5;
          stepExtraInfo = 6;
        } else if (this.typePaid == "") {
          step = 4;
          stepExtraInfo = 5;
        }
      }

      return { stepTime: step, stepExtraInfo: stepExtraInfo };
    },
    allowedDates() {
      let disable_dates = [];

      this.daysRules.forEach((element) => {
        if (element.data === false) {
          disable_dates.push(
            moment(element.date, "YYYY-MM-DD").format("YYYY-MM-DD")
          );
        }
      });

      const disabledDates = {
        to: moment().subtract(1, "days").format("YYYY-MM-DD"),
        dates: disable_dates,
      };

      return (val) => {
        if (disabledDates.to && val <= disabledDates.to) {
          return false;
        }

        if (disabledDates.dates.includes(val)) {
          return false;
        }

        return true;
      };
    },
    inputDate: {
      get() {
        return this.input_date
          ? moment(this.input_date).format("YYYY-MM-DD")
          : null;
      },
      set(val) {
        this.input_date = new Date(val);
      },
    },
    selectedData() {
      let players = null;
      let hoyos = null;

      players = this.playerConf.filter(
        (player) => player.value == this.playersNumber
      )[0];

      hoyos = this.tiposJuego.filter((hoyos) => hoyos.id == this.hoyos)[0];

      return { players, hoyos };
    },
    sectionsTarifas() {
      return this.compTarifas.flatMap((tarifa) =>
        tarifa.HorariosVisibles.map((horario, index) => ({
          tarifa,
          horario,
          index,
        }))
      );
    },
    isPlayersInvalid() {
      if (this.changeGuest) {
        return this.players.some((p) => !p.name || !p.email);
      } else {
        const first = this.players[0];
        return !first.name || !first.email;
      }
    },
  },
  async mounted() {
    this.showPrefered();
    this.playersNumber = "1";
    this.language = navigator.language || navigator.userLanguage;
    this.Mercados = await this.getMercados();
    this.Segmentos = await this.getSegmentos();


    //this.testRealTime();
  },
  beforeMount() {
    this.MainDepartamento = "Golf";
    this.getConfig();
    this.$store.dispatch("listenerTiposCambio");
    if (this.isHotel) {
      this.prefered = true;
    }
    if (this.isWeb) {
      this.$store.commit("setTypePaid", 2);
    }

    if (!this.isWeb && !this.isHotel) {
      this.$store.commit("setTypePaid", 2);
      this.isAdmin = true;
    }

    this.$store.commit("setGlobalPrice", 190);
    this.init();
  },
  created() {
    setInterval(() => {
      let fecha = new moment();
      this.actualDate = fecha; //.format("YYYY-MM-DD HH:mm:ss");
    }, 1000);
  },
};
</script>

<style scoped>
/* ========================================================================= */
/* Booking Base & Typography                                                 */
/* ========================================================================= */
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

.booking-page {
  font-family: 'Inter', sans-serif;
  background-color: #F8FAFC;
  min-height: 100vh;
  position: relative;
  padding-bottom: 120px;
  /* Space for fixed footer */
}

h1,
h2,
h3,
h4,
h5,
h6,
.hero-title,
.card-heading__title {
  font-family: 'Outfit', sans-serif;
  letter-spacing: -0.02em;
}

/* ========================================================================= */
/* Hero Header & Overlap                                                     */
/* ========================================================================= */
.hero-header {
  position: relative;
  height: 320px;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.hero-header__bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1;
}

.hero-header__video {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.hero-header__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(15, 23, 42, 0.4) 0%, rgba(15, 23, 42, 0.8) 100%);
  z-index: 2;
}

.hero-header__content {
  position: relative;
  z-index: 3;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
}

.hero-nav {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding-top: 8px;
  /* For safe area */
}

.hero-titles {
  margin-top: auto;
  margin-bottom: 40px;
  /* Space for the overlap card */
}

.hero-title {
  color: white;
  font-size: 2.2rem;
  font-weight: 800;
  margin: 0 0 4px 0;
  line-height: 1.1;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin: 0;
  font-weight: 400;
}

.hero-location {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  padding: 4px 12px;
  border-radius: 100px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
}

/* ========================================================================= */
/* Main Overlapping Container                                                */
/* ========================================================================= */
.booking-main-container {
  position: relative;
  z-index: 4;
  background: #F8FAFC;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  margin-top: -32px;
  min-height: 500px;
  padding: 24px 16px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
}

.booking-inner-wrap {
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

/* ========================================================================= */
/* Stepper (Mockup Style)                                                    */
/* ========================================================================= */
.mockup-stepper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  max-width: 400px;
  margin: 0 auto 32px auto;
  position: relative;
}

.mockup-stepper__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  cursor: pointer;
  flex: 1;
}

.mockup-stepper__circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #E2E8F0;
  color: #64748B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.mockup-stepper__circle.active {
  background: #059669;
  color: white;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.mockup-stepper__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94A3B8;
  text-align: center;
  transition: color 0.3s ease;
}

.mockup-stepper__label.active {
  color: #0F172A;
}

.mockup-stepper__line {
  flex-grow: 1;
  height: 2px;
  background: #E2E8F0;
  margin-top: 15px;
  /* Half of circle height */
  z-index: 1;
  min-width: 20px;
  transition: background 0.3s ease;
}

.mockup-stepper__line.active {
  background: #059669;
}

/* ========================================================================= */
/* Mockup Quick Filters (Mobile)                                             */
/* ========================================================================= */
.mockup-filters {
  display: flex;
  gap: 12px;
  width: 100%;
}

.mockup-filter-btn {
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: all 0.2s ease;
}

.mockup-filter-btn:active {
  background: #F1F5F9;
  transform: scale(0.98);
}

.mockup-filter-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0F172A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ========================================================================= */
/* Mockup Pill Toggles (Tee Time / Twilight)                                 */
/* ========================================================================= */
.mockup-pill-toggles {
  display: flex;
  background: #F1F5F9;
  border-radius: 100px;
  padding: 4px;
  gap: 4px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.mockup-pill-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  padding: 10px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748B;
  transition: all 0.2s ease;
  border: none;
  background: transparent;
  cursor: pointer;
}

.mockup-pill-btn--active {
  background: #059669;
  color: white;
  box-shadow: 0 2px 8px rgba(5, 150, 105, 0.2);
}

/* ========================================================================= */
/* Desktop Config Capsule                                                    */
/* ========================================================================= */
.config-capsule {
  border-radius: 100px !important;
  border: 1px solid #E2E8F0;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04) !important;
}

.capsule-field {
  display: flex;
  flex-direction: column;
}

.capsule-field__label {
  font-size: 0.65rem;
  font-weight: 800;
  color: #059669;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  padding-left: 12px;
}

/* ========================================================================= */
/* Generic UI Components                                                     */
/* ========================================================================= */
.modern-card {
  background: #FFFFFF !important;
  border-radius: 20px !important;
  border: 1px solid #E2E8F0;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04) !important;
  overflow: hidden;
}

.card-heading {
  display: flex;
  align-items: flex-start;
}

.card-heading__icon {
  background: #ECFDF5;
  border-radius: 12px;
  padding: 10px;
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-heading__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  margin: 0 0 4px 0;
}

.card-heading__sub {
  font-size: 0.85rem;
  color: #64748B;
  margin: 0;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}

/* Divider */
.divider-subtle {
  border-color: #F1F5F9 !important;
}

/* Error Banner */
.modern-error-banner {
  background: #FEF2F2;
  border: 1px solid #FCA5A5;
  border-radius: 12px;
  padding: 12px 16px;
  color: #991B1B;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}

/* Button */
.modern-btn-primary {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  border-radius: 100px !important;
  box-shadow: 0 8px 24px -6px rgba(5, 150, 105, 0.4) !important;
}

.modern-btn-primary:disabled {
  background-color: #a8a8a81f !important;
  color: #94A3B8 !important;
  box-shadow: none !important;
}

/* Inputs */
.modern-input>>>.v-input__control>.v-input__slot {
  background: #F8FAFC !important;
  border-color: #E2E8F0 !important;
  border-radius: 12px !important;
  min-height: 48px;
}

.modern-input>>>.v-text-field__details {
  display: none !important;
}

/* Datepicker */
.modern-datepicker-wrap>>>.v-picker__body {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.04);
}

/* ========================================================================= */
/* Player Select Segmented Buttons                                           */
/* ========================================================================= */
.players-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.player-choice-btn {
  background: #F8FAFC;
  border: 2px solid #E2E8F0;
  border-radius: 16px;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  cursor: pointer;
}

.player-choice-btn:hover {
  border-color: #94A3B8;
  background: #059669;
}

.player-choice-btn--active {
  background: #059669;
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

.player-choice-btn__val {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0F172A;
  line-height: 1.1;
  margin-top: 4px;
}

.player-choice-btn--active .player-choice-btn__val,
.player-choice-btn--active .player-choice-btn__label {
  color: white;
}

.player-choice-btn__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748B;
  margin-top: 2px;
}

/* ========================================================================= */
/* Hotel Range Cards                                                         */
/* ========================================================================= */
.range-card {
  position: relative;
  background: #F8FAFC;
  border: 2px solid #E2E8F0;
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 100%;
}

.range-card:hover {
  border-color: #94A3B8;
}

.range-card--active {
  background: #ECFDF5;
  border-color: #059669;
}

.range-card__check {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #CBD5E1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.range-card--active .range-card__check {
  background: #059669;
  border-color: #059669;
}

.range-card__badge {
  display: inline-block;
  background: #D1FAE5;
  color: #065F46;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 100px;
  margin-bottom: 8px;
}

.range-card__badge--direct {
  background: #FEE2E2;
  color: #991B1B;
}

.range-card__name {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F172A;
}

.range-card__desc {
  font-size: 0.8rem;
  color: #64748B;
}

/* ========================================================================= */
/* Checkout Elements                                                         */
/* ========================================================================= */
.checkout-wrap {
  padding-bottom: 40px;
}

.checkout-recap-banner {
  background: #0F172A;
  border-radius: 16px;
  padding: 16px 20px;
  color: white;
  box-shadow: 0 10px 30px -10px rgba(15, 23, 42, 0.5);
}

.checkout-recap-banner__title {
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 2px;
}

.checkout-recap-banner__sub {
  font-size: 0.85rem;
  color: #94A3B8;
  font-weight: 500;
}

.change-time-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  background: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.dress-code-banner {
  background: #FFFBEB;
  border: 1px solid #FEF3C7;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
}

.dress-code-icon {
  background: #FEF3C7;
  padding: 8px;
  border-radius: 12px;
  margin-right: 16px;
}

.dress-code-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #92400E;
  margin-bottom: 4px;
}

.dress-code-text {
  font-size: 0.8rem;
  color: #B45309;
  line-height: 1.4;
}

/* Player Card */
.player-card__header {
  background: #F8FAFC;
  padding: 12px 20px;
  border-bottom: 1px solid #E2E8F0;
}

.player-card__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #059669;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  margin-right: 12px;
}

.player-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F172A;
}

.player-card__badge {
  background: #D1FAE5;
  color: #065F46;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 100px;
  text-transform: uppercase;
}

/* Addons */
.addon-toggle-box {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 12px 16px;
}

.addon-price-tag {
  background: #ECFDF5;
  color: #059669;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Summary */
.summary-card__header {
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
}

.summary-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F172A;
  margin: 0;
}

.dual-currency-box {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F8FAFC;
  border-radius: 16px;
  padding: 20px;
}

.dual-currency-box__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.dual-currency-box__divider {
  width: 1px;
  height: 40px;
  background: #E2E8F0;
}

.currency-tag {
  font-size: 0.8rem;
  font-weight: 700;
  color: #64748B;
  margin-bottom: 4px;
}

.currency-amount {
  font-size: 1.75rem;
  font-weight: 800;
  color: #0A5C36;
  font-family: 'Outfit', sans-serif;
  line-height: 1;
}

/* Sticky Footer */
.checkout-fixed-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-top: 1px solid #E2E8F0;
  padding: 16px 24px;
  z-index: 50;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);
}

.checkout-footer-content {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.checkout-footer-price {
  display: flex;
  flex-direction: column;
}

.checkout-footer-price__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 2px;
}

.checkout-footer-price__val {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0F172A;
  font-family: 'Outfit', sans-serif;
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.usd-text {
  font-size: 0.9rem;
  color: #64748B;
  font-weight: 600;
}

.pesos-text {
  font-size: 0.85rem;
  color: #94A3B8;
  font-weight: 500;
  margin-left: 4px;
}

/* Dialog Styles */
.dialog-header {
  background: #0F172A;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.dialog-header__title {
  font-family: 'Outfit', sans-serif;
  font-size: 1.2rem;
  font-weight: 700;
}

.rider-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #F8FAFC;
  border-radius: 12px;
  margin-bottom: 8px;
  border: 1px solid #E2E8F0;
}

/* Loader Overlay */
.booking-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 24px 24px 0 0;
}

.booking-loader-card {
  background: white;
  padding: 32px 48px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.1);
  text-align: center;
}

.booking-loader-text {
  margin-top: 16px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0F172A;
}

.booking-loader-sub {
  font-size: 0.85rem;
  color: #64748B;
}

/* Adjustments for desktop cards */
@media (min-width: 960px) {
  .hero-header {
    height: 400px;
  }

  .booking-main-container {
    margin-top: -64px;
    padding: 32px;
  }
}

/* Override Vuetify Tabs bg issue */
.v-tabs-items {
  background-color: transparent !important;
}

.transparent {
  background-color: transparent !important;
}
</style>
